import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Loader2, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGES = [
  { id: "individual", label: "Standard Individual Tax Return", amount: 650 },
  { id: "self_employed", label: "Self-Employed / Schedule C", amount: 850 },
  { id: "complex", label: "Complex Individual Return", amount: 1000 },
];

export default function Payments() {
  const [selected, setSelected] = useState("individual");
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pay = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    const isCustom = selected === "custom";
    if (isCustom && (!customAmount || parseFloat(customAmount) < 1)) {
      toast.error("Please enter a valid amount (at least $1).");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: form.name, email: form.email, phone: form.phone,
        note: form.note, origin_url: window.location.origin,
      };
      if (isCustom) body.amount = parseFloat(customAmount);
      else body.package_id = selected;
      const res = await axios.post(`${API}/payments/checkout`, body);
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout. Please try again or call 201-253-7674.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="payments-page">
      <section className="bg-navy text-white noise-overlay py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">Secure Payments</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Make a Payment</h1>
          <p className="font-body text-lg text-gray-300">
            Pay your tax preparation fee securely by card. Choose a service or enter the amount you were quoted.
          </p>
        </div>
      </section>

      <section className="bg-bone py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={pay} className="bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-6">
            <div>
              <Label className="text-navy font-semibold mb-3 block">Select what you're paying for</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PACKAGES.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setSelected(p.id)}
                    className={`text-left rounded-xl border-2 p-4 transition-colors ${selected === p.id ? "border-bronze bg-bone" : "border-gray-200 hover:border-bronze/50"}`}
                    data-testid={`pay-pkg-${p.id}`}
                  >
                    <div className="font-body font-semibold text-navy text-sm">{p.label}</div>
                    <div className="font-heading text-lg font-bold text-bronze">${p.amount}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setSelected("custom")}
                  className={`text-left rounded-xl border-2 p-4 transition-colors ${selected === "custom" ? "border-bronze bg-bone" : "border-gray-200 hover:border-bronze/50"}`}
                  data-testid="pay-pkg-custom"
                >
                  <div className="font-body font-semibold text-navy text-sm">Other Amount</div>
                  <div className="font-body text-xs text-gray-500">Enter a quoted / invoiced amount</div>
                </button>
              </div>
            </div>

            {selected === "custom" && (
              <div>
                <Label htmlFor="pay-amount">Amount (USD) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input id="pay-amount" type="number" min="1" step="0.01" className="pl-7" data-testid="pay-amount" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="0.00" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div><Label htmlFor="pay-name">Full Name *</Label><Input id="pay-name" data-testid="pay-name" value={form.name} onChange={(e) => up("name", e.target.value)} /></div>
              <div><Label htmlFor="pay-phone">Phone *</Label><Input id="pay-phone" data-testid="pay-phone" value={form.phone} onChange={(e) => up("phone", e.target.value)} /></div>
            </div>
            <div><Label htmlFor="pay-email">Email *</Label><Input id="pay-email" type="email" data-testid="pay-email" value={form.email} onChange={(e) => up("email", e.target.value)} /></div>
            <div><Label htmlFor="pay-note">Invoice # / Note (optional)</Label><Input id="pay-note" data-testid="pay-note" value={form.note} onChange={(e) => up("note", e.target.value)} /></div>

            <Button type="submit" disabled={submitting} className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 text-lg font-medium" data-testid="pay-submit">
              {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirecting to secure checkout...</> : <><CreditCard className="w-5 h-5 mr-2" /> Continue to Secure Payment</>}
            </Button>
            <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
              <Lock className="w-3 h-3" /> Payments are processed securely by Stripe. We never see or store your card details.
            </p>
          </form>
          <div className="flex items-center justify-center gap-2 mt-6 text-gray-500">
            <ShieldCheck className="w-4 h-4 text-green-600" />
            <span className="font-body text-sm">PCI-compliant checkout powered by Stripe</span>
          </div>
        </div>
      </section>
    </div>
  );
}
